import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Eye, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import CategoriesSection from "@/components/CategoriesSection";

const CATEGORIES_FALLBACK = ["Domestic Gas", "Commercial Gas", "Electrical", "Oil", "Plumbing", "Low Carbon & Net Zero", "Retrofit & Compliance", "ECO & Energy Efficiency"];
const catColor = {
  "Domestic Gas": "bg-blue-100 text-blue-700 border-blue-200",
  "Commercial Gas": "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Electrical": "bg-amber-100 text-amber-700 border-amber-200",
  "Oil": "bg-orange-100 text-orange-700 border-orange-200",
  "Plumbing": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Low Carbon & Net Zero": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Retrofit & Compliance": "bg-violet-100 text-violet-700 border-violet-200",
  "ECO & Energy Efficiency": "bg-teal-100 text-teal-700 border-teal-200",
};

export default function QualificationsPage() {
  useDocumentTitle("Qualifications", "Configure the qualifications you deliver.");
  const { user } = useAuth();
  const [quals, setQuals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", code: "", accreditation_body: "", description: "", duration_days: 1, certificate_validity_years: 0, default_certification_fee: 0 });

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch (err) { console.error("Failed to load categories:", err); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const fetchQuals = useCallback(async () => {
    try {
      const params = category !== "All" ? `?category=${encodeURIComponent(category)}` : "";
      const { data } = await api.get(`/qualifications${params}`);
      setQuals(data);
    } catch (err) { if (!isAuthError(err)) toast.error("Failed to load qualifications"); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { fetchQuals(); }, [fetchQuals]);

  const defaultCat = categories[0]?.name || "";
  const openCreate = () => { setEditing(null); setForm({ name: "", category: defaultCat, code: "", accreditation_body: "", description: "", duration_days: 1, certificate_validity_years: 0, default_certification_fee: 0 }); setDialogOpen(true); };
  const openEdit = (q) => { setEditing(q); setForm({ name: q.name, category: q.category, code: q.code || "", accreditation_body: q.accreditation_body || "", description: q.description || "", duration_days: q.duration_days || 1, certificate_validity_years: q.certificate_validity_years || 0, default_certification_fee: q.default_certification_fee || 0 }); setDialogOpen(true); };

  const viewDetail = async (q) => {
    try {
      const { data } = await api.get(`/qualifications/${q.id}`);
      setDetail(data); setDetailOpen(true);
    } catch { toast.error("Failed to load details"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/qualifications/${editing.id}`, form);
        toast.success("Qualification updated");
      } else {
        await api.post("/qualifications", form);
        toast.success("Qualification created");
      }
      setDialogOpen(false); fetchQuals();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`Delete ${q.name}?`)) return;
    try {
      await api.delete(`/qualifications/${q.id}`);
      toast.success("Qualification deleted"); fetchQuals();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isSuper = user?.role === "superadmin";
  const [showCategories, setShowCategories] = useState(false);

  return (
    <div className="space-y-4" data-testid="qualifications-page">
      {/* Qualification Categories (collapsible) */}
      <div className="border border-border rounded-md bg-white">
        <button
          type="button"
          onClick={() => setShowCategories(v => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-slate-50/60 transition-colors"
          data-testid="toggle-categories-section"
        >
          <span className="flex items-center gap-2">
            {showCategories ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Manage Qualification Categories
            <Badge variant="outline" className="text-[10px]">{categories.length}</Badge>
          </span>
          <span className="text-xs text-muted-foreground">{isAdmin ? "Add, edit or rename" : "View only"}</span>
        </button>
        {showCategories && (
          <div className="px-4 py-4 border-t border-border">
            <CategoriesSection isAdmin={isAdmin} onChanged={() => { fetchCategories(); fetchQuals(); }} />
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="overflow-x-auto">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList className="h-8" data-testid="qual-category-tabs">
              <TabsTrigger value="All" className="text-xs px-3 py-1">All</TabsTrigger>
              {(categories.length ? categories.map(c => c.name) : CATEGORIES_FALLBACK).map(c => <TabsTrigger key={c} value={c} className="text-xs px-3 py-1">{c}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>
        {isAdmin ? (
          <div className="flex items-center gap-2">
            {isSuper && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!window.confirm("Seed the standard UK training qualifications catalogue? This is safe to run more than once — existing codes are skipped.")) return;
                  try {
                    const { data } = await api.post("/qualifications/seed-defaults");
                    toast.success(data.message || "Seeded");
                    fetchQuals();
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Seed failed");
                  }
                }}
                data-testid="seed-defaults-btn"
              >
                Seed UK catalogue
              </Button>
            )}
            <Button onClick={() => openCreate()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-qual-btn"><Plus size={16} className="mr-1" /> Add Qualification</Button>
          </div>
        ) : (
          <Badge variant="outline" className="text-[10px] text-muted-foreground border-dashed shrink-0" data-testid="qual-readonly-badge">Read-only · Admin to edit</Badge>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground animate-pulse">Loading...</div>
      ) : quals.length === 0 ? (
        <div className="border border-border rounded-md p-8 text-center text-muted-foreground bg-white">No qualifications found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quals.map((q, i) => (
            <div key={q.id} className="border border-border rounded-md p-4 bg-white animate-fade-in hover:border-slate-300 transition-colors" style={{ animationDelay: `${i * 40}ms` }} data-testid={`qual-card-${q.id}`}>
              <div className="flex items-start justify-between mb-2">
                <Badge variant="outline" className={`text-[10px] ${catColor[q.category] || ""}`}>{q.category}</Badge>
                <div className="flex gap-1">
                  <button onClick={() => viewDetail(q)} className="p-1 rounded hover:bg-slate-100" data-testid={`view-qual-${q.id}`}><Eye size={14} /></button>
                  {isAdmin && <button onClick={() => openEdit(q)} className="p-1 rounded hover:bg-slate-100" data-testid={`edit-qual-${q.id}`}><Pencil size={14} /></button>}
                  {isAdmin && <button onClick={() => handleDelete(q)} className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`delete-qual-${q.id}`}><Trash2 size={14} /></button>}
                </div>
              </div>
              <h4 className="font-heading font-semibold text-sm text-foreground mb-1">{q.name}</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                {q.code && <span>Code: <strong className="text-foreground">{q.code}</strong></span>}
                {q.accreditation_body && <span>Accredited: <strong className="text-foreground">{q.accreditation_body}</strong></span>}
                <span>Duration: <strong className="text-foreground">{q.duration_days}d</strong></span>
                {q.certificate_validity_years > 0 && <span>Valid: <strong className="text-foreground">{q.certificate_validity_years}yr</strong></span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="qual-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{detail.name}</DialogTitle>
                <Badge variant="outline" className={`w-fit text-[10px] ${catColor[detail.category] || ""}`}>{detail.category}</Badge>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">{detail.description || "No description"}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                <div className="border border-border rounded-md p-3 text-center"><p className="text-2xl font-bold text-foreground">{detail.stats?.total_enrolled || 0}</p><p className="text-xs text-muted-foreground">Enrolled</p></div>
                <div className="border border-border rounded-md p-3 text-center"><p className="text-2xl font-bold text-emerald-600">{detail.stats?.passed || 0}</p><p className="text-xs text-muted-foreground">Passed</p></div>
                <div className="border border-border rounded-md p-3 text-center"><p className="text-2xl font-bold text-sky-600">{detail.stats?.active_certificates || 0}</p><p className="text-xs text-muted-foreground">Active Certs</p></div>
                <div className="border border-border rounded-md p-3 text-center"><p className="text-2xl font-bold text-amber-600">{detail.stats?.expiring_certificates || 0}</p><p className="text-xs text-muted-foreground">Expiring</p></div>
              </div>

              {/* Booked courses list */}
              <div className="mt-4 flex-1 min-h-0 overflow-y-auto -mx-6 px-6 border-t border-border pt-3">
                <h4 className="text-sm font-heading font-semibold mb-2">Courses booked under this qualification</h4>
                {(detail.courses || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">No courses have been scheduled against this qualification yet.</p>
                ) : (
                  <div className="space-y-3">
                    {detail.courses.map(c => (
                      <div key={c.id} className="border border-border rounded-md p-3" data-testid={`qual-course-${c.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <div>
                            <p className="text-sm font-medium">{c.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.start_date}{c.end_date && c.end_date !== c.start_date ? ` → ${c.end_date}` : ""}
                              {c.instructor ? ` · ${c.instructor}` : ""}
                              {c.location ? ` · ${c.location}` : ""}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {c.enrolled}/{c.max_capacity}
                          </Badge>
                        </div>
                        {c.learners && c.learners.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.learners.map(l => (
                              <a
                                key={l.member_id}
                                href={l.member_id ? `/members?open=${l.member_id}` : "#"}
                                className="text-[11px] bg-slate-100 hover:bg-slate-200 border border-border rounded-full px-2 py-0.5"
                                title={l.status || ""}
                              >
                                {l.member_name || l.member_email || "—"}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="qual-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Qualification" : "Add Qualification"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="qual-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="qual-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{(categories.length ? categories.map(c => c.name) : CATEGORIES_FALLBACK).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="qual-code-input" /></div>
            </div>
            <div><Label className="text-xs">Accreditation Body</Label><Input value={form.accreditation_body} onChange={(e) => setForm({ ...form, accreditation_body: e.target.value })} data-testid="qual-accreditation-input" /></div>
            <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="qual-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Duration (days)</Label><Input type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })} data-testid="qual-duration-input" /></div>
              <div><Label className="text-xs">Validity (years)</Label><Input type="number" min="0" value={form.certificate_validity_years} onChange={(e) => setForm({ ...form, certificate_validity_years: parseInt(e.target.value) || 0 })} data-testid="qual-validity-input" /></div>
            </div>
            <div><Label className="text-xs">Default Certification Fee (£)</Label><Input type="number" min="0" step="0.01" value={form.default_certification_fee} onChange={(e) => setForm({ ...form, default_certification_fee: parseFloat(e.target.value) || 0 })} data-testid="qual-cert-fee-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="qual-submit-btn">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
